import { PROSPECT_CHANNELS, buildPremiumProperties, deriveProspectChannel } from '@ourbranch/offer-utils';

// Shared property builder for the per-prospect funnel — all "Staff Funnel: X"
// events. Fires through account.sendEventAsMember keyed on offer.webUserId so
// the prospect threads as a single Amplitude user across all funnel steps,
// from rater (Generated Offer in Rater) through to Policy Purchased. webUserId
// is a deterministic hash of name + address, consistent across rater and
// frontend invocations.
//
// Rejection codes live on quote.offerings (per OFFER_FRAGMENT). The rejections
// array lives on offer. Property names mirror the existing
// `Generated Offer in Rater` event so analysts can filter the same way across
// the full funnel.

// Mirrors collectRejections in
// appsync-be/lambdas/rater-v2/src/managers/log.manager/log.manager.ts so the
// funnel events flatten to the same `rejections.home` / `rejections.auto`
// Amplitude properties as Generated Offer in Rater and Failed Offer
// (e.g. `rejections.home contains 50006`), instead of the raw
// {type, value, data, level} array which Amplitude can't query.
const summarizeRejections = (rejections) => {
  if (!Array.isArray(rejections)) {
    return undefined;
  }
  const summary = rejections.reduce((acc, rejection) => {
    // Same truthy check as collectRejections (drops a falsy value like 0 —
    // real rejection codes are 5xxxx, so nothing legitimate is lost).
    if (!rejection?.type || !rejection?.value) {
      return acc;
    }
    acc[rejection.type] = acc[rejection.type] || [];
    acc[rejection.type].push(rejection.value);
    return acc;
  }, {});
  return Object.keys(summary).length ? summary : undefined;
};

// Who is at the keyboard, as opposed to the offer's sales attribution
// (isAgentSold/affinity): external agency users are inferred from Cognito
// affinity attributes (session.isAgency), and actorAgencyCode is the
// logged-in user's own affinity code from custom:affinity_code.
// actorLicensedInState says whether that user holds a license for the
// offer's state (session.allowedStates ← custom:allowed_states) — i.e.
// whether the person looking at the offer could actually sell it.
export const buildActorProperties = (session, state) =>
  session
    ? {
        actorType: session.isAgency ? 'external-agent' : 'internal-staff',
        actorAgencyCode: session.user?.affinityCode || undefined,
        ...(state && Array.isArray(session.allowedStates)
          ? { actorLicensedInState: session.allowedStates.includes(state) }
          : {})
      }
    : {};

// The comparative-rater deep link is /offer/<offerId>?rater=ez&type=HA. The params only
// survive on the landing URL, so they're read at event time: present on the bridge landing's
// Offer Viewed, absent afterwards. Free text from the partner — never validated server-side.
const readBridgeParams = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  return {
    bridgeRaterParam: params.get('rater') || undefined,
    bridgeOptionParam: params.get('type') || undefined
  };
};

// The channel rule is shared with the pre-login Bridge Link Opened endpoint (external-api-be).
export { PROSPECT_CHANNELS, deriveProspectChannel };

export const buildProspectFunnelProperties = ({ offer, session, extraProps = {} }) => {
  const offerings = offer?.quote?.offerings;
  const pricedOption = extraProps.selectedOption ?? offer?.quote?.selectedOption;
  return {
    webUserId: offer?.webUserId,
    offerId: offer?.id,
    staffRep: session?.user?.username,
    isAgency: session?.isAgency,
    ...buildActorProperties(session, offer?.quote?.correctedAddress?.state),
    affinity: offer?.quote?.global?.affinity || '',
    affinityStem: offer?.quote?.global?.affinityGroupStem,
    state: offer?.quote?.correctedAddress?.state,
    leadSource: offer?.quote?.leadSource,
    isAgentSold: offer?.quote?.isAgentSold,
    fromStaff: offer?.quote?.fromStaff,
    originallyFrom: offer?.quote?.originallyFrom,
    simulatedSource: offer?.quote?.simulationConfig?.source,
    selectedOption: offer?.quote?.selectedOption,
    // Same {A, H, HA, AR} availability map Generated Offer in Rater sends
    // (log.manager.ts), so per-stage policy-type mix is queryable.
    eligiblePolicyTypes: offerings
      ? {
          A: offerings.offerAuto,
          H: offerings.offerHomeowners,
          HA: offerings.offerAuto && offerings.offerHomeowners,
          AR: offerings.offerRenters
        }
      : undefined,
    rejections: summarizeRejections(offer?.rejections),
    autoRejectCode: offerings?.autoRejectCode,
    monolineAutoRejectCode: offerings?.monolineAutoRejectCode,
    homeownersRejectCode: offerings?.homeownersRejectCode,
    condoRejectCode: offerings?.condoRejectCode,
    // BIX paper + Branch brand (quote.isBix), so BIX-live filtering doesn't need a state list.
    isBix: offer?.quote?.isBix,
    // Rater attribution from the offer record (platform-sanctioned key prefix, not a credential)
    // — survives when the ?rater= URL param is lost. fromRC1 is the stored flag.
    apiKeyFirst5: offer?.quote?.global?.apiKeyFirst5 || undefined,
    fromRC1: offer?.quote?.global?.fromRC1,
    ...readBridgeParams(),
    ...buildPremiumProperties(offer, pricedOption),
    ...extraProps
  };
};

export const trackProspectFunnelEvent = async ({ sendEventAsMember, eventName, offer, session, extraProps = {} }) => {
  const webUserId = offer?.webUserId;
  if (!webUserId || !sendEventAsMember) {
    return;
  }
  const prospectChannel = deriveProspectChannel(offer);
  return sendEventAsMember({
    eventName,
    userId: webUserId,
    properties: JSON.stringify(buildProspectFunnelProperties({ offer, session, extraProps })),
    // Sent on every funnel event; the Segment Amplitude destination lists these under "Traits to
    // Set Once", so Amplitude keeps only the first value and the prospect's first touch in Staff
    // decides their sub-channel permanently. Adding a key here means adding it to that list too.
    ...(prospectChannel && {
      setOnceUserProperties: JSON.stringify({
        prospectChannel,
        prospectChannelEvent: eventName,
        prospectChannelOfferId: offer?.id,
        prospectChannelSetAt: new Date().toISOString()
      })
    })
  });
};
